import {
  type GameState,
  type OneNightAction,
  type ProjectedViewerState,
  projectGameStateForViewer,
} from "@cloudnest/one-night-ultimate-werewolf-engine";
import { applyOneNightAction, validateOneNightAction } from "./action-router";
import {
  type OneNightReplayEvent,
  toOneNightReplayEvent,
} from "./event-adapter";
import type { OneNightReplayFixtureEvent } from "./replay-fixtures.generated";

export interface OneNightReplaySnapshot {
  readonly index: number;
  readonly event: OneNightReplayFixtureEvent;
  readonly replayEvent: OneNightReplayEvent;
  readonly state: GameState;
  readonly observerView: ProjectedViewerState;
}

export interface BuildOneNightReplayOptions {
  readonly observerUserId?: string;
}

function requireState(
  state: GameState | null,
  eventType: OneNightReplayEvent["eventType"],
): GameState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function bindActor(action: OneNightAction, senderId: string): OneNightAction {
  return { ...action, actorPlayerId: senderId };
}

function applyReplayEvent(
  state: GameState | null,
  event: OneNightReplayEvent,
): GameState {
  if (event.eventType === "init") {
    return event.payload.state;
  }

  const current = requireState(state, event.eventType);
  if (event.eventType === "SyncViewerState") {
    return current;
  }

  const action = bindActor(event.payload.action, event.userId);
  const validation = validateOneNightAction(current, action);
  if (!validation.valid) {
    throw new Error(
      `${action.type} rejected: ${validation.error ?? "Validation failed"}`,
    );
  }

  return applyOneNightAction(current, action);
}

/**
 * Recap-only projection: every card face is visible. Never render this as the
 * default view — the masked observer projection is the default (§5 board
 * idiom, board-game-ux-ui-standard).
 */
export function projectGodView(state: GameState): ProjectedViewerState {
  const base = projectGameStateForViewer(state, "recap-god-view");
  return {
    ...base,
    visibleCards: Object.fromEntries(
      state.cards.map((card) => [
        card.id,
        {
          cardId: card.id,
          roleId: card.roleId,
          visibility: "known" as const,
        },
      ]),
    ),
  };
}

export function buildOneNightReplaySnapshots(
  events: readonly OneNightReplayFixtureEvent[],
  options: BuildOneNightReplayOptions = {},
): readonly OneNightReplaySnapshot[] {
  const observerUserId = options.observerUserId ?? "observer";
  const snapshots: OneNightReplaySnapshot[] = [];
  let state: GameState | null = null;

  for (const event of events) {
    const replayEvent = toOneNightReplayEvent(event);
    state = applyReplayEvent(state, replayEvent);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
      observerView: projectGameStateForViewer(state, observerUserId),
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty One Night event log");
  }

  return snapshots;
}
